import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Home, BookOpen, FileText, Brain, Trophy, Clock, Target, Zap, Sparkles, Play, Volume2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

// Stages that use "Revision" instead of "Mock"
const REVISION_STAGES = ['year-9', 'year-10', 'gcse', 'a-levels'];

// Educational videos for 7+ Entry
const educationalVideos = [
  { id: 'v1', title: 'Number Bonds to 10', thumbnail: '🔢', duration: '4:32', description: 'Learn number bonds with fun animations', embedId: 'DnFrOetuUKg' },
  { id: 'v2', title: 'Phonics Fun', thumbnail: '🔤', duration: '5:15', description: 'Learn letter sounds with songs', embedId: 'hq3yfQnllfQ' },
  { id: 'v3', title: 'Shapes All Around', thumbnail: '🔷', duration: '3:48', description: 'Discover 2D and 3D shapes', embedId: 'OEbRDtCAFdU' },
  { id: 'v4', title: 'Counting Adventures', thumbnail: '🎯', duration: '4:20', description: 'Count with friendly animals', embedId: 'DR-cfDsHCGA' },
];

// Math Game Component for 7+ Entry
const MathGame = ({ type }) => {
  const [num1, setNum1] = useState(Math.floor(Math.random() * 10) + 1);
  const [num2, setNum2] = useState(Math.floor(Math.random() * (type === 'multiplication' ? 5 : 10)) + 1);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  const animals = ['🐶', '🐱', '🐰', '🦊', '🐼', '🐨', '🦁', '🐸'];
  const [currentAnimal] = useState(animals[Math.floor(Math.random() * animals.length)]);

  const getCorrectAnswer = () => {
    if (type === 'addition') return num1 + num2;
    if (type === 'subtraction') return Math.max(num1, num2) - Math.min(num1, num2);
    if (type === 'multiplication') return num1 * num2;
    return 0;
  };

  const checkAnswer = () => {
    const correct = getCorrectAnswer();
    if (parseInt(userAnswer) === correct) {
      setFeedback({ correct: true, message: '🎉 Amazing! Well done!' });
      setScore(s => s + 10);
      setStreak(s => s + 1);
    } else {
      setFeedback({ correct: false, message: `Not quite! The answer is ${correct}` });
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    setNum1(Math.floor(Math.random() * 10) + 1);
    setNum2(Math.floor(Math.random() * (type === 'multiplication' ? 5 : 10)) + 1);
    setUserAnswer('');
    setFeedback(null);
  };

  const displayNum1 = type === 'subtraction' ? Math.max(num1, num2) : num1;
  const displayNum2 = type === 'subtraction' ? Math.min(num1, num2) : num2;
  const operator = type === 'addition' ? '+' : type === 'subtraction' ? '-' : '×';

  return (
    <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-3xl">{currentAnimal}</span>
          <div className="text-right">
            <p className="text-xs text-gray-600">Score: <span className="font-bold text-green-600">{score}</span></p>
            {streak > 0 && <p className="text-xs text-orange-600">🔥 {streak} streak!</p>}
          </div>
        </div>
        
        <div className="text-center mb-4">
          <p className="text-3xl font-bold text-gray-800">
            {displayNum1} {operator} {displayNum2} = ?
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input
            type="number"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            className="w-full sm:flex-1 p-3 text-center text-xl border-2 border-yellow-300 rounded-lg focus:border-yellow-500 focus:outline-none"
            placeholder="?"
            disabled={feedback !== null}
          />
          {!feedback ? (
            <Button 
              onClick={checkAnswer} 
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 py-3" 
              disabled={!userAnswer}
              data-testid="math-game-check-btn"
            >
              Check ✓
            </Button>
          ) : (
            <Button 
              onClick={nextQuestion} 
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 py-3"
              data-testid="math-game-next-btn"
            >
              Next →
            </Button>
          )}
        </div>

        {feedback && (
          <div className={`text-center p-2 rounded-lg ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {feedback.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SubjectDashboard = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedPattern, setSelectedPattern] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState(['Mathematics', 'English', 'Reasoning', 'Verbal Reasoning', 'Non-Verbal Reasoning']); // All expanded by default
  
  // Toggle group expansion
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => 
      prev.includes(groupName) 
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };
  
  // Check if this stage uses "Revision" label
  const isRevisionStage = REVISION_STAGES.includes(categoryId);
  const paperTypeLabel = isRevisionStage ? 'revision' : 'mock';

  const categoryData = {
    'seven-plus': {
      title: '7+ Entry Exams',
      gradient: 'from-blue-500 to-cyan-500',
      subjects: [
        {
          id: 'maths-7',
          name: 'Mathematics',
          icon: '🔢',
          color: 'bg-blue-100 border-blue-300',
          description: 'Foundation mathematics for Year 2/3 students',
          topics: ['Number & Place Value', 'Addition & Subtraction', 'Multiplication & Division', 'Fractions', 'Measurement', 'Geometry', 'Statistics'],
          mockPapers: 18,
          practiceQuestions: 470,
          duration: '30 minutes',
          difficulty: 'Foundation'
        },
        {
          id: 'english-7',
          name: 'English',
          icon: '📚',
          color: 'bg-green-100 border-green-300',
          description: 'Reading comprehension and creative writing',
          topics: ['Reading Comprehension', 'Creative Writing', 'Spelling', 'Grammar', 'Punctuation', 'Vocabulary'],
          mockPapers: 19,
          practiceQuestions: 490,
          duration: '30 minutes',
          difficulty: 'Foundation'
        },
        {
          id: 'reasoning-7',
          name: 'Reasoning',
          icon: '🧩',
          color: 'bg-purple-100 border-purple-300',
          description: 'Logical thinking and problem-solving',
          topics: ['Pattern Recognition', 'Sequences', 'Analogies', 'Logic Puzzles', 'Spatial Reasoning', 'Classification', 'Deduction'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '25 minutes',
          difficulty: 'Foundation to Intermediate'
        }
      ]
    },
    'eleven-plus': {
      title: '11+ Entry Exams',
      gradient: 'from-purple-500 to-indigo-500',
      hasPatterns: true,
      patterns: [
        {
          id: 'sutton-set',
          name: 'Sutton SET',
          fullName: 'Sutton Selective Eligibility Test',
          description: 'Competitive test for Sutton grammar schools',
          color: 'from-purple-500 to-indigo-500'
        },
        {
          id: 'cem',
          name: 'CEM',
          fullName: 'Durham University CEM 11+',
          description: 'Used by Kent, Bucks, and Warwickshire',
          color: 'from-blue-500 to-cyan-500'
        },
        {
          id: 'gl',
          name: 'GL Assessment',
          fullName: 'GL Assessment 11+',
          description: 'Traditional format used by many schools',
          color: 'from-green-500 to-emerald-500'
        },
        {
          id: 'iseb',
          name: 'ISEB',
          fullName: 'ISEB Common Pre-Test',
          description: 'Used by many independent schools',
          color: 'from-orange-500 to-red-500'
        }
      ],
      subjects: [
        // Sutton SET
        {
          id: 'maths-sutton',
          name: 'Mathematics',
          pattern: 'Sutton SET',
          patternId: 'sutton-set',
          icon: '📐',
          color: 'bg-purple-100 border-purple-300',
          description: 'Adaptive maths for Sutton grammar schools',
          topics: ['Arithmetic', 'Algebra', 'Geometry', 'Fractions & Decimals', 'Percentages', 'Problem Solving', 'Logic & Reasoning'],
          mockPapers: 20,
          practiceQuestions: 660,
          duration: '45 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'english-sutton',
          name: 'English',
          pattern: 'Sutton SET',
          patternId: 'sutton-set',
          icon: '✍️',
          color: 'bg-purple-100 border-purple-300',
          description: 'Reading comprehension for Sutton SET',
          topics: ['Reading Comprehension', 'Vocabulary', 'Grammar', 'Literary Techniques'],
          mockPapers: 10,
          practiceQuestions: 250,
          duration: '45 minutes',
          difficulty: 'Intermediate'
        },
        // CEM
        {
          id: 'verbal-reasoning-cem',
          name: 'Verbal Reasoning',
          pattern: 'CEM',
          patternId: 'cem',
          icon: '💭',
          color: 'bg-blue-100 border-blue-300',
          description: 'Mixed verbal reasoning with comprehension',
          topics: ['Comprehension', 'Vocabulary', 'Spelling', 'Grammar', 'Word Problems'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '45 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'numerical-reasoning-cem',
          name: 'Numerical Reasoning',
          pattern: 'CEM',
          patternId: 'cem',
          icon: '🔢',
          color: 'bg-blue-100 border-blue-300',
          description: 'Numerical problem-solving and reasoning',
          topics: ['Number Operations', 'Problem Solving', 'Data Interpretation', 'Patterns', 'Mental Math'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '45 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'non-verbal-reasoning-cem',
          name: 'Non-Verbal Reasoning',
          pattern: 'CEM',
          patternId: 'cem',
          icon: '🔷',
          color: 'bg-blue-100 border-blue-300',
          description: 'Visual and spatial reasoning patterns',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Matrix Problems'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '30 minutes',
          difficulty: 'Intermediate'
        },
        // GL Assessment
        {
          id: 'english-gl',
          name: 'English',
          pattern: 'GL Assessment',
          patternId: 'gl',
          icon: '📚',
          color: 'bg-green-100 border-green-300',
          description: 'Comprehensive English language test',
          topics: ['Comprehension', 'Grammar', 'Punctuation', 'Vocabulary', 'Spelling', 'Sentence Completion'],
          mockPapers: 10,
          practiceQuestions: 250,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'maths-gl',
          name: 'Mathematics',
          pattern: 'GL Assessment',
          patternId: 'gl',
          icon: '📐',
          color: 'bg-green-100 border-green-300',
          description: 'Standard mathematical reasoning',
          topics: ['Arithmetic', 'Algebra', 'Geometry', 'Fractions', 'Decimals', 'Percentages', 'Data Handling', 'Logic & Reasoning'],
          mockPapers: 20,
          practiceQuestions: 660,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'verbal-reasoning-gl',
          name: 'Verbal Reasoning',
          pattern: 'GL Assessment',
          patternId: 'gl',
          icon: '💭',
          color: 'bg-green-100 border-green-300',
          description: 'Traditional verbal reasoning questions',
          topics: ['Word Meanings', 'Letter Patterns', 'Word Relationships', 'Code Breaking', 'Logic'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'non-verbal-reasoning-gl',
          name: 'Non-Verbal Reasoning',
          pattern: 'GL Assessment',
          patternId: 'gl',
          icon: '🔷',
          color: 'bg-green-100 border-green-300',
          description: 'Visual and spatial problem solving',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Rotations', 'Reflections', 'Codes'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        // ISEB
        {
          id: 'english-iseb',
          name: 'English',
          pattern: 'ISEB',
          patternId: 'iseb',
          icon: '📚',
          color: 'bg-orange-100 border-orange-300',
          description: 'Adaptive English comprehension and language',
          topics: ['Reading Comprehension', 'Grammar', 'Vocabulary', 'Spelling'],
          mockPapers: 10,
          practiceQuestions: 250,
          duration: '25 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'maths-iseb',
          name: 'Mathematics',
          pattern: 'ISEB',
          patternId: 'iseb',
          icon: '📐',
          color: 'bg-orange-100 border-orange-300',
          description: 'Adaptive mathematical reasoning',
          topics: ['Number', 'Algebra', 'Geometry', 'Statistics', 'Problem Solving', 'Logic & Reasoning'],
          mockPapers: 20,
          practiceQuestions: 660,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'verbal-reasoning-iseb',
          name: 'Verbal Reasoning',
          pattern: 'ISEB',
          patternId: 'iseb',
          icon: '💭',
          color: 'bg-orange-100 border-orange-300',
          description: 'Verbal logic and reasoning',
          topics: ['Word Patterns', 'Analogies', 'Logic', 'Sequences'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '36 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'non-verbal-reasoning-iseb',
          name: 'Non-Verbal Reasoning',
          pattern: 'ISEB',
          patternId: 'iseb',
          icon: '🔷',
          color: 'bg-orange-100 border-orange-300',
          description: 'Spatial and visual reasoning',
          topics: ['Shape Patterns', 'Sequences', 'Analogies', 'Matrix Problems'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '32 minutes',
          difficulty: 'Intermediate'
        }
      ]
    },
    'year-9': {
      title: 'Year 9',
      gradient: 'from-teal-500 to-cyan-500',
      subjects: [
        {
          id: 'maths-y9',
          name: 'Mathematics',
          icon: '📐',
          color: 'bg-blue-100 border-blue-300',
          description: 'KS3 Mathematics consolidation',
          topics: ['Number', 'Algebra', 'Ratio & Proportion', 'Geometry', 'Probability', 'Statistics'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '60 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'english-y9',
          name: 'English',
          icon: '📚',
          color: 'bg-green-100 border-green-300',
          description: 'Reading, writing and language skills',
          topics: ['Reading Comprehension', 'Creative Writing', 'Grammar', 'Punctuation', 'Vocabulary', 'Literary Techniques', 'Poetry', 'Text Analysis'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '60 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'science-y9',
          name: 'Science',
          icon: '🔬',
          color: 'bg-purple-100 border-purple-300',
          description: 'Combined Biology, Chemistry and Physics',
          topics: ['Cells & Organisation', 'Atoms & Elements', 'Forces & Motion', 'Energy', 'Chemical Reactions', 'Ecosystems'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '60 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'history-y9',
          name: 'History',
          icon: '🏛️',
          color: 'bg-amber-100 border-amber-300',
          description: 'British and world history',
          topics: ['Medieval England', 'Tudor Period', 'Industrial Revolution', 'World War I', 'World War II', 'Source Skills'],
          mockPapers: 8,
          practiceQuestions: 300,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        },
        {
          id: 'geography-y9',
          name: 'Geography',
          icon: '🌍',
          color: 'bg-cyan-100 border-cyan-300',
          description: 'Physical and human geography',
          topics: ['Weather & Climate', 'Rivers', 'Coasts', 'Population', 'Urbanisation', 'Map Skills'],
          mockPapers: 8,
          practiceQuestions: 300,
          duration: '50 minutes',
          difficulty: 'Intermediate'
        }
      ]
    },
    'year-10': {
      title: 'Year 10',
      gradient: 'from-indigo-500 to-purple-500',
      subjects: [
        {
          id: 'maths-y10',
          name: 'Mathematics',
          icon: '📊',
          color: 'bg-blue-100 border-blue-300',
          description: 'GCSE Foundation Year Mathematics',
          topics: ['Number', 'Algebra', 'Ratio & Proportion', 'Geometry', 'Probability', 'Statistics', 'Trigonometry'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '75 minutes',
          difficulty: 'Foundation/Higher'
        },
        {
          id: 'english-y10',
          name: 'English',
          icon: '📖',
          color: 'bg-green-100 border-green-300',
          description: 'GCSE English Language & Literature prep',
          topics: ['Reading Comprehension', 'Creative Writing', 'Language Analysis', 'Poetry', 'Prose', 'Drama'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '75 minutes',
          difficulty: 'Foundation/Higher'
        },
        {
          id: 'science-y10',
          name: 'Science',
          icon: '🧪',
          color: 'bg-purple-100 border-purple-300',
          description: 'GCSE Combined Science preparation',
          topics: ['Cell Biology', 'Atomic Structure', 'Forces', 'Energy', 'Chemical Reactions', 'Ecology', 'Waves'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '75 minutes',
          difficulty: 'Foundation/Higher'
        },
        {
          id: 'history-y10',
          name: 'History',
          icon: '🏛️',
          color: 'bg-amber-100 border-amber-300',
          description: 'GCSE History preparation',
          topics: ['Medicine Through Time', 'Elizabethan England', 'Weimar Germany', 'Cold War', 'Source Analysis'],
          mockPapers: 10,
          practiceQuestions: 350,
          duration: '60 minutes',
          difficulty: 'Standard'
        },
        {
          id: 'geography-y10',
          name: 'Geography',
          icon: '🌍',
          color: 'bg-cyan-100 border-cyan-300',
          description: 'GCSE Geography preparation',
          topics: ['Living World', 'Physical Landscapes', 'Urban Issues', 'Resource Management', 'Fieldwork'],
          mockPapers: 10,
          practiceQuestions: 350,
          duration: '60 minutes',
          difficulty: 'Standard'
        }
      ]
    },
    'gcse': {
      title: 'GCSE',
      gradient: 'from-green-500 to-emerald-500',
      subjects: [
        {
          id: 'maths-gcse',
          name: 'Mathematics',
          icon: '📊',
          color: 'bg-blue-100 border-blue-300',
          description: 'GCSE Mathematics (Foundation & Higher)',
          topics: ['Number', 'Algebra', 'Ratio & Proportion', 'Geometry', 'Probability', 'Statistics', 'Trigonometry', 'Graphs'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '90 minutes',
          difficulty: 'Foundation & Higher'
        },
        {
          id: 'english-lang-gcse',
          name: 'English Language',
          icon: '📖',
          color: 'bg-green-100 border-green-300',
          description: 'Reading, writing and language analysis',
          topics: ['Reading Comprehension', 'Creative Writing', 'Transactional Writing', 'Language Analysis', 'Spoken Language'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '105 minutes',
          difficulty: 'Foundation & Higher'
        },
        {
          id: 'english-lit-gcse',
          name: 'English Literature',
          icon: '📕',
          color: 'bg-teal-100 border-teal-300',
          description: 'Shakespeare, poetry and modern texts',
          topics: ['Shakespeare', 'Modern Texts', '19th Century Novel', 'Poetry', 'Modern Drama', 'Literary Analysis', 'Language Analysis'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '105 minutes',
          difficulty: 'Foundation & Higher'
        },
        {
          id: 'sciences-gcse',
          name: 'Combined Science',
          icon: '🔬',
          color: 'bg-purple-100 border-purple-300',
          description: 'Biology, Chemistry and Physics',
          topics: ['Cell Biology', 'Atomic Structure', 'Forces', 'Energy', 'Chemical Reactions', 'Ecology', 'Waves', 'Genetics'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '75 minutes per paper',
          difficulty: 'Foundation & Higher'
        },
        {
          id: 'history-gcse',
          name: 'History',
          icon: '🏛️',
          color: 'bg-amber-100 border-amber-300',
          description: 'British and world history topics',
          topics: ['Medicine Through Time', 'Elizabethan England', 'Weimar & Nazi Germany', 'Cold War', 'Source Analysis'],
          mockPapers: 8,
          practiceQuestions: 350,
          duration: '90 minutes',
          difficulty: 'Standard'
        },
        {
          id: 'geography-gcse',
          name: 'Geography',
          icon: '🌍',
          color: 'bg-cyan-100 border-cyan-300',
          description: 'Physical and human geography',
          topics: ['Living World', 'Physical Landscapes', 'Urban Issues', 'Resource Management', 'Fieldwork', 'Issue Evaluation'],
          mockPapers: 8,
          practiceQuestions: 360,
          duration: '90 minutes',
          difficulty: 'Standard'
        }
      ]
    },
    'a-levels': {
      title: 'A-Levels',
      gradient: 'from-orange-500 to-red-500',
      subjects: [
        {
          id: 'maths-a',
          name: 'Mathematics',
          icon: '∫',
          color: 'bg-blue-100 border-blue-300',
          description: 'Pure, Statistics and Mechanics',
          topics: ['Calculus', 'Algebra', 'Trigonometry', 'Vectors', 'Statistics', 'Probability', 'Mechanics', 'Differential Equations'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '120 minutes',
          difficulty: 'Advanced'
        },
        {
          id: 'further-maths-a',
          name: 'Further Mathematics',
          icon: '∑',
          color: 'bg-indigo-100 border-indigo-300',
          description: 'Advanced mathematical concepts',
          topics: ['Complex Numbers', 'Matrices', 'Further Calculus', 'Differential Equations', 'Hyperbolic Functions', 'Polar Coordinates'],
          mockPapers: 16,
          practiceQuestions: 500,
          duration: '120 minutes',
          difficulty: 'Advanced'
        },
        {
          id: 'physics-a',
          name: 'Physics',
          icon: '⚛️',
          color: 'bg-purple-100 border-purple-300',
          description: 'Classical and modern physics',
          topics: ['Mechanics', 'Electricity', 'Waves', 'Quantum Physics', 'Thermodynamics', 'Fields', 'Nuclear Physics', 'Astrophysics'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '120 minutes',
          difficulty: 'Advanced'
        },
        {
          id: 'chemistry-a',
          name: 'Chemistry',
          icon: '🧪',
          color: 'bg-green-100 border-green-300',
          description: 'Organic, inorganic and physical chemistry',
          topics: ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Thermodynamics', 'Kinetics', 'Equilibria', 'Redox'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '120 minutes',
          difficulty: 'Advanced'
        },
        {
          id: 'biology-a',
          name: 'Biology',
          icon: '🧬',
          color: 'bg-emerald-100 border-emerald-300',
          description: 'Molecular biology and ecology',
          topics: ['Cell Biology', 'Biochemistry', 'Genetics', 'Evolution', 'Ecology', 'Human Physiology', 'Plant Biology'],
          mockPapers: 18,
          practiceQuestions: 450,
          duration: '120 minutes',
          difficulty: 'Advanced'
        },
        {
          id: 'english-lit-a',
          name: 'English Literature',
          icon: '📚',
          color: 'bg-rose-100 border-rose-300',
          description: 'Critical analysis of literature',
          topics: ['Poetry', 'Drama', 'Prose', 'Unseen Texts', 'Critical Theory', 'Comparative Analysis', 'Context & Interpretation'],
          mockPapers: 20,
          practiceQuestions: 500,
          duration: '150 minutes',
          difficulty: 'Advanced'
        }
      ]
    }
  };

  const currentCategory = categoryData[categoryId];
  const currentSubject = selectedSubject ? currentCategory?.subjects.find(s => s.id === selectedSubject) : null;
  
  // Get feature mode from location state (from ExploreLanding)
  const location = useLocation();
  const featureMode = location.state?.feature; // 'practice-library' or 'challenger'
  
  // Handle subject click based on feature mode
  const handleSubjectClick = (subject) => {
    if (featureMode === 'practice-library') {
      // Direct to papers
      navigate(`/papers/${categoryId}/${subject.id}`);
    } else if (featureMode === 'challenger') {
      // Direct to challenger mode
      navigate(`/challenger/${categoryId}/${subject.id}`);
    } else {
      // Default - show subject details
      setSelectedSubject(subject.id);
    }
  };
  
  // Filter subjects by pattern for 11+
  const filteredSubjects = currentCategory?.hasPatterns && selectedPattern !== 'all'
    ? currentCategory.subjects.filter(s => s.patternId === selectedPattern)
    : currentCategory?.subjects || [];

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-4">Category not found</p>
            <Button onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${currentCategory.gradient} text-white py-6 px-4 md:px-8 shadow-lg`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/20"
              data-testid="back-btn"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/categories')}
              className="text-white hover:bg-white/20"
            >
              <Home className="mr-1 h-4 w-4" /> Home
            </Button>
          </div>
          {featureMode && (
            <Badge className="mb-3 bg-white/20 text-white border-white/30 text-sm">
              {featureMode === 'practice-library' ? '📚 Practice Library' : '🎮 Challenger Mode'}
            </Badge>
          )}
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{currentCategory.title}</h1>
          <p className="text-white/90 mt-2">
            {featureMode === 'practice-library' 
              ? 'Select a subject to access practice papers'
              : featureMode === 'challenger' 
                ? 'Select a subject to start Challenger Mode'
                : `Select a subject to view ${paperTypeLabel} papers and practice materials`
            }
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row max-w-7xl mx-auto p-4 md:p-8 gap-6">
        {/* Left Sidebar - Subject Tiles */}
        <div className="w-full md:w-72 space-y-3">
          <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {currentCategory.hasPatterns ? 'Exam Patterns' : 'Subjects'}
          </h2>
          
          {/* Pattern filter tabs for 11+ */}
          {currentCategory.hasPatterns && (
            <div className="mb-4 space-y-2">
              <Button
                variant={selectedPattern === 'all' ? 'default' : 'outline'}
                className={`w-full justify-start ${selectedPattern === 'all' ? 'bg-white text-purple-700' : 'bg-white/20 text-white border-white/30 hover:bg-white/30'}`}
                onClick={() => { setSelectedPattern('all'); setSelectedSubject(null); }}
                data-testid="pattern-all"
              >
                All Exam Patterns
              </Button>
              {currentCategory.patterns.map((pattern) => (
                <Button
                  key={pattern.id}
                  variant={selectedPattern === pattern.id ? 'default' : 'outline'}
                  className={`w-full justify-start ${selectedPattern === pattern.id ? 'bg-white text-purple-700' : 'bg-white/20 text-white border-white/30 hover:bg-white/30'}`}
                  onClick={() => { setSelectedPattern(pattern.id); setSelectedSubject(null); }}
                  data-testid={`pattern-${pattern.id}`}
                >
                  <div className="text-left">
                    <div className="font-semibold">{pattern.name}</div>
                    <div className="text-xs opacity-75">{pattern.description}</div>
                  </div>
                </Button>
              ))}
              <div className="border-t border-white/30 pt-3 mt-3">
                <p className="text-white/80 text-sm mb-2">Subjects</p>
              </div>
            </div>
          )}
          
          {/* Grouped view for "All Exam Patterns" in 11+ */}
          {currentCategory.hasPatterns && selectedPattern === 'all' ? (
            // Group subjects by name
            (() => {
              const groupedSubjects = filteredSubjects.reduce((acc, subject) => {
                const groupName = subject.name;
                if (!acc[groupName]) acc[groupName] = [];
                acc[groupName].push(subject);
                return acc;
              }, {});
              
              return Object.entries(groupedSubjects).map(([groupName, subjects]) => (
                <div key={groupName} className="mb-2">
                  {/* Group Header */}
                  <button
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all mb-1"
                    onClick={() => toggleGroup(groupName)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{subjects[0].icon}</span>
                      <span className="font-semibold">{groupName}</span>
                      <Badge className="bg-white/20 text-white text-xs">{subjects.length}</Badge>
                    </div>
                    {expandedGroups.includes(groupName) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Expanded Subjects */}
                  {expandedGroups.includes(groupName) && (
                    <div className="space-y-2 pl-2">
                      {subjects.map((subject) => (
                        <Card
                          key={subject.id}
                          className={`cursor-pointer transition-all hover:shadow-lg ${
                            selectedSubject === subject.id ? 'ring-2 ring-white shadow-xl' : ''
                          }`}
                          onClick={() => handleSubjectClick(subject)}
                          data-testid={`subject-${subject.id}`}
                        >
                          <CardContent className={`p-3 ${subject.color} border-2`}>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-purple-600 font-medium">{subject.pattern}</p>
                                <p className="text-xs text-gray-600">{subject.mockPapers + Math.ceil(subject.practiceQuestions / 25)} total papers</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ));
            })()
          ) : (
            // Normal flat list for other views
            filteredSubjects.map((subject) => (
              <Card
                key={subject.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  selectedSubject === subject.id ? 'ring-2 ring-white shadow-xl' : ''
                }`}
                onClick={() => handleSubjectClick(subject)}
                data-testid={`subject-${subject.id}`}
              >
                <CardContent className={`p-4 ${subject.color} border-2`}>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{subject.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 truncate">{subject.name}</p>
                      {subject.pattern && selectedPattern === 'all' && (
                        <p className="text-xs text-purple-600 font-medium">{subject.pattern}</p>
                      )}
                      <p className="text-xs text-gray-600">{subject.mockPapers + Math.ceil(subject.practiceQuestions / 25)} total papers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}

          {/* STEM Library moved to Resources Hub - accessible via /resources-hub for Year 9-10/GCSE students */}
        </div>

        {/* Right Content Area */}
        <div className="flex-1">
          {currentSubject ? (
            <Card className="shadow-2xl animate-fade-in" data-testid="subject-details">
              <CardHeader className={`bg-gradient-to-r ${currentCategory.gradient} text-white rounded-t-lg`}>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-3xl font-bold mb-2">
                      <span className="text-4xl mr-3">{currentSubject.icon}</span>
                      {currentSubject.name}
                    </CardTitle>
                    <CardDescription className="text-white/90 text-base">
                      {currentSubject.description}
                    </CardDescription>
                    {currentSubject.pattern && (
                      <Badge className="mt-2 bg-white/20 text-white border-white/30">
                        {currentSubject.pattern}
                      </Badge>
                    )}
                  </div>
                  <Badge className="bg-white text-purple-700 font-semibold px-3 py-1">
                    {currentSubject.difficulty}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <FileText className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{currentSubject.mockPapers}</p>
                      <p className="text-xs text-gray-600">{isRevisionStage ? 'Revision' : 'Mock'} Papers</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{currentSubject.practiceQuestions}</p>
                      <p className="text-xs text-gray-600">Questions</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4 text-center">
                      <Clock className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm font-bold text-purple-700">{currentSubject.duration}</p>
                      <p className="text-xs text-gray-600">Duration</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4 text-center">
                      <Brain className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-orange-700">{currentSubject.topics.length}</p>
                      <p className="text-xs text-gray-600">Topics</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tabs for different sections */}
                <Tabs defaultValue="topics" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="topics">Topics Covered</TabsTrigger>
                    <TabsTrigger value="practice">Practice</TabsTrigger>
                    <TabsTrigger value="mock">{isRevisionStage ? 'Revision' : 'Mock'} Papers</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="topics" className="space-y-4 mt-4">
                    <h3 className="font-semibold text-lg">Topics Covered</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {currentSubject.topics.map((topic, idx) => (
                        <Card key={idx} className="border-2 hover:border-purple-300 transition-colors">
                          <CardContent className="p-3 flex items-center gap-2">
                            <div className="bg-purple-100 text-purple-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </div>
                            <span className="text-sm font-medium">{topic}</span>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="practice" className="space-y-4 mt-4">
                    <h3 className="font-semibold text-lg">Practice by Topic</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Practice specific topics with instant feedback and explanations.
                    </p>
                    <div className="grid gap-3">
                      {currentSubject.topics.slice(0, 6).map((topic, idx) => (
                        <Card key={idx} className="hover:shadow-lg transition-all cursor-pointer border-2 hover:border-green-400">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="bg-green-100 text-green-700 rounded-lg px-3 py-2 font-bold text-sm">
                                {Math.floor(currentSubject.practiceQuestions / currentSubject.topics.length)} Qs
                              </div>
                              <div>
                                <p className="font-semibold">{topic}</p>
                                <p className="text-sm text-gray-600">Mixed difficulty</p>
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                              Practice →
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="mock" className="space-y-4 mt-4">
                    <h3 className="font-semibold text-lg">{isRevisionStage ? 'Revision' : 'Mock Examination'} Papers</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Full-length examination papers designed to simulate real exam conditions. 
                      Complete with mark schemes and detailed solutions.
                    </p>
                    <div className="grid gap-3">
                      {[...Array(Math.min(5, currentSubject.mockPapers))].map((_, idx) => (
                        <Card key={idx} className="hover:shadow-lg transition-all cursor-pointer border-2 hover:border-purple-400">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="bg-purple-600 text-white rounded-lg w-12 h-12 flex items-center justify-center font-bold">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-semibold">{isRevisionStage ? 'Revision' : 'Mock'} Paper {idx + 1}</p>
                                <p className="text-sm text-gray-600">{currentSubject.duration} • {Math.floor(currentSubject.practiceQuestions / currentSubject.mockPapers)} questions</p>
                              </div>
                            </div>
                            <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                              Start →
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                      {currentSubject.mockPapers > 5 && (
                        <p className="text-center text-sm text-gray-600">+ {currentSubject.mockPapers - 5} more papers available</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 pt-4">
                  <div className="flex gap-4">
                    <Button 
                      onClick={() => navigate(`/papers/${categoryId}/${currentSubject.id}`)}
                      className="flex-1 h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      <Trophy className="mr-2 h-5 w-5" />
                      View All Papers ({currentSubject.mockPapers + Math.ceil(currentSubject.practiceQuestions / 25)})
                    </Button>
                  </div>
                  
                  {/* Challenger Button - Available for 11+, Year 9, Year 10, GCSE, A-Levels */}
                  {['eleven-plus', 'year-9', 'year-10', 'gcse', 'a-levels'].includes(categoryId) && (
                    <Button 
                      onClick={() => navigate(`/challenger/${categoryId}/${currentSubject.id}`)}
                      className="w-full h-14 text-lg bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 text-white font-bold shadow-lg"
                      data-testid="challenger-button"
                    >
                      <Zap className="mr-2 h-6 w-6" />
                      🔥 CHALLENGER MODE
                    </Button>
                  )}
                  
                  {/* Reading Challenge - Available for 7+ and 11+ */}
                  {['seven-plus', 'eleven-plus'].includes(categoryId) && (
                    <Button 
                      onClick={() => navigate(`/reading-challenge/${categoryId === 'seven-plus' ? '7-plus' : '11-plus'}`)}
                      className="w-full h-14 text-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold shadow-lg"
                      data-testid="reading-challenge-button"
                    >
                      <BookOpen className="mr-2 h-6 w-6" />
                      📖 READING CHALLENGE
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-xl">
              <CardContent className="p-12 text-center">
                <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Select a Subject</h3>
                <p className="text-gray-500">Choose a subject from the left to view {paperTypeLabel} papers and practice materials</p>
              </CardContent>
            </Card>
          )}

          {/* Fun Learning Zone - Only for 7+ Entry */}
          {categoryId === 'seven-plus' && (
            <Card className="mt-8 shadow-xl bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Sparkles className="h-6 w-6 text-yellow-500" /> Fun Learning Zone
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Math Games */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🎮</span> Interactive Math Games
                  </h4>
                  <p className="text-gray-600 mb-4">Practice basic maths with fun animal friends! Perfect for 6-7 year olds.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-center font-semibold mb-2">➕ Addition Fun</p>
                      <MathGame type="addition" />
                    </div>
                    <div>
                      <p className="text-center font-semibold mb-2">➖ Subtraction Fun</p>
                      <MathGame type="subtraction" />
                    </div>
                    <div>
                      <p className="text-center font-semibold mb-2">✖️ Times Tables</p>
                      <MathGame type="multiplication" />
                    </div>
                  </div>
                </div>

                {/* Educational Videos */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">📺</span> Learning Videos
                  </h4>
                  <p className="text-gray-600 mb-4">Watch fun cartoons that make learning exciting!</p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {educationalVideos.map((video) => (
                      <Card 
                        key={video.id} 
                        className="bg-white border-gray-200 hover:border-blue-400 transition-all cursor-pointer group overflow-hidden"
                      >
                        <div className="relative">
                          <div className="aspect-video bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                            <span className="text-6xl">{video.thumbnail}</span>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <a 
                                href={`https://www.youtube.com/watch?v=${video.embedId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-full transition-transform group-hover:scale-110"
                              >
                                <Play className="h-8 w-8 fill-white" />
                              </a>
                            </div>
                          </div>
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                            {video.duration}
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <h5 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-1">{video.title}</h5>
                          <p className="text-gray-500 text-xs">{video.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Fun Facts */}
                <Card className="bg-gradient-to-r from-purple-100 to-pink-100 border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <span className="text-4xl">🌈</span>
                      <div>
                        <h4 className="text-lg font-bold text-gray-800 mb-2">Did You Know?</h4>
                        <p className="text-gray-700">
                          Learning maths through games helps children develop problem-solving skills 3x faster than traditional methods! 
                          Our fun activities are designed by education experts to make every child fall in love with numbers. 
                          Keep playing, keep learning! 🚀
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectDashboard;
